import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlertTriangleIcon,
  BanIcon,
  CheckCircle2Icon,
  ClockIcon,
  DownloadIcon,
  Loader2Icon,
  XCircleIcon,
} from 'lucide-react';
import { Header } from '../components/Header';
import { useCase } from '../contexts/CaseContext';
import { nextPollInterval } from '../lib/intakePollingCadence';
import {
  cancelExport,
  fetchExports,
  fetchExportDownloadUrl,
  type ExportJob,
  type ExportJobStatus,
  type ExportPackage,
} from '../lib/database';
import { StatusPill as UiStatusPill, type StatusTone, Button } from '../ui';

/** Row title per package, matching the card the export was started from. */
export const PACKAGE_LABEL: Record<ExportPackage, string> = {
  native: 'Original files',
  review_package: 'Review package',
  combined_pdf: 'Single combined PDF',
  production: 'Production package',
  table: 'Table',
};

// ══════════════════════════════════════════════════════════════════════
// Exports page (#763)
// ══════════════════════════════════════════════════════════════════════
//
// The list of background export jobs for a case. Structure follows the
// discovery All Files tab, which is the house pattern for "rows that finish on
// their own": adaptive polling that backs off as a job ages, a status pill with
// a per-status action, and cancel behind a spam guard.
//
// The one thing this page must not do is collapse `ready_with_exceptions` into
// either success or failure. The artifact EXISTS and is downloadable, and some
// documents did not make it — a reviewer handing the ZIP to someone else needs
// to know both halves, so the pill and the count line say so separately.

const LABEL: Record<ExportJobStatus, string> = {
  queued: 'Queued',
  building: 'Building',
  ready: 'Ready',
  ready_with_exceptions: 'Ready with exceptions',
  failed: 'Failed',
  cancelled: 'Cancelled',
  expired: 'Expired',
};

const STATUS_TONE: Record<ExportJobStatus, StatusTone> = {
  queued: 'neutral',
  building: 'running',
  ready: 'ready',
  // Amber, not green: the file is usable but incomplete, and a green pill
  // invites nobody to read the exceptions.
  ready_with_exceptions: 'running',
  failed: 'danger',
  cancelled: 'neutral',
  expired: 'neutral',
};

const ICON: Record<ExportJobStatus, typeof ClockIcon> = {
  queued: ClockIcon,
  building: Loader2Icon,
  ready: CheckCircle2Icon,
  ready_with_exceptions: AlertTriangleIcon,
  failed: XCircleIcon,
  cancelled: BanIcon,
  expired: ClockIcon,
};

const ACTIVE: ReadonlySet<ExportJobStatus> = new Set(['queued', 'building']);
const DOWNLOADABLE: ReadonlySet<ExportJobStatus> = new Set(['ready', 'ready_with_exceptions']);

export function formatBytes(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
  if (bytes >= 1024 ** 2) return `${Math.round(bytes / 1024 ** 2)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** `N of M · K exceptions` — both numbers, because the difference is the point. */
/**
 * When the poll cadence's clock starts. Backoff is measured from the moment the
 * page started watching the jobs that are currently running, so a job that
 * becomes active later restarts it — otherwise a job queued while an old one
 * has been building for an hour is watched at the fully backed-off rate.
 */
export function pollClock(
  watching: ReadonlySet<string>,
  jobs: ExportJob[],
  startedAt: number,
  now: number,
): { active: Set<string>; startedAt: number } {
  const active = new Set(jobs.filter((j) => ACTIVE.has(j.status)).map((j) => j.id));
  const newlyActive = [...active].some((id) => !watching.has(id));
  return { active, startedAt: newlyActive ? now : startedAt };
}

export function progressLine(job: ExportJob): string {
  if (job.status === 'ready_with_exceptions') {
    return `${job.docs_succeeded.toLocaleString()} of ${job.docs_total.toLocaleString()} documents · `
      + `${job.docs_failed.toLocaleString()} exception${job.docs_failed === 1 ? '' : 's'}`;
  }
  if (job.status === 'ready') {
    return `${job.docs_total.toLocaleString()} documents`;
  }
  if (ACTIVE.has(job.status)) {
    return `${job.docs_total.toLocaleString()} documents queued`;
  }
  return `${job.docs_total.toLocaleString()} documents requested`;
}

export function StatusPill({ status }: { status: ExportJobStatus }) {
  const Icon = ICON[status];
  return (
    <UiStatusPill
      tone={STATUS_TONE[status]}
      size="md"
      className="font-semibold"
      icon={<Icon className={`w-3.5 h-3.5 ${status === 'building' ? 'animate-spin' : ''}`} />}
    >
      {LABEL[status]}
    </UiStatusPill>
  );
}

export function ExportsScreen() {
  const caseData = useCase();

  const [jobs, setJobs] = useState<ExportJob[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-row, so cancelling one job does not disable the others' buttons.
  const [busyIds, setBusyIds] = useState<Set<string>>(new Set());

  const timer = useRef<number | null>(null);
  const startedAt = useRef<number>(Date.now());
  const watching = useRef<Set<string>>(new Set());

  const load = useCallback(async () => {
    try {
      const { exports } = await fetchExports(caseData.id);
      setJobs(exports);
      setError(null);
      return exports;
    } catch (err: any) {
      setError(err?.body?.message || err?.message || 'Could not load exports.');
      return null;
    }
  }, [caseData.id]);

  // Poll only while something is actually moving, and back off as it ages —
  // a multi-GB ZIP can run for many minutes, and a fixed 2s poll would issue
  // hundreds of requests per open tab for no added responsiveness.
  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      const rows = await load();
      if (cancelled) return;
      const clock = pollClock(watching.current, rows ?? [], startedAt.current, Date.now());
      watching.current = clock.active;
      startedAt.current = clock.startedAt;
      if (clock.active.size === 0) return;
      timer.current = window.setTimeout(
        tick, nextPollInterval(Date.now() - startedAt.current),
      );
    };

    tick();
    return () => {
      cancelled = true;
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [load]);

  const withBusy = useCallback(async (id: string, fn: () => Promise<void>) => {
    setBusyIds((prev) => new Set(prev).add(id));
    try {
      await fn();
    } finally {
      setBusyIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  }, []);

  const handleDownload = useCallback((job: ExportJob) => withBusy(job.id, async () => {
    try {
      // Minted per click and short-lived, so it is followed immediately and
      // never stored.
      const { url } = await fetchExportDownloadUrl(caseData.id, job.id);
      window.location.assign(url);
    } catch (err: any) {
      setError(err?.body?.message || err?.message || 'Could not start the download.');
      await load();
    }
  }), [caseData.id, load, withBusy]);

  const handleCancel = useCallback((job: ExportJob) => withBusy(job.id, async () => {
    try {
      await cancelExport(caseData.id, job.id);
    } catch (err: any) {
      setError(err?.body?.message || err?.message || 'Could not cancel this export.');
    }
    await load();
  }), [caseData.id, load, withBusy]);

  return (
    <div className="min-h-screen bg-navy flex flex-col">
      {/* Header takes only the case name: #795 removed pageContext and #818
          replaced the chevron with the breadcrumb trail's named exits, so the
          way back is a crumb rather than a prop. The h1 below is the page
          title. */}
      <Header caseName={caseData.case_name} />

      <main id="main" tabIndex={-1} className="flex-1 px-6 py-8 max-w-5xl w-full mx-auto">
        <h1 className="font-serif text-2xl text-white">Exports</h1>
        <p className="mt-1 text-sm text-gray-400">
          Files you have asked for. Downloads stay available for a limited time.
        </p>

        {error && (
          <div
            role="alert"
            className="mt-5 flex gap-3 rounded-xl border border-rose-400/50 bg-rose-400/10 px-3.5 py-3"
          >
            <AlertTriangleIcon className="w-5 h-5 shrink-0 mt-0.5 text-rose-300" />
            <p className="text-sm text-gray-100 leading-snug">{error}</p>
          </div>
        )}

        {jobs === null && !error && (
          <p className="mt-8 text-sm text-gray-400">Loading…</p>
        )}

        {jobs?.length === 0 && (
          <p className="mt-8 text-sm text-gray-400">
            No exports yet. Filter the documents you want, then choose Export.
          </p>
        )}

        <ul className="mt-6 space-y-2">
          {(jobs ?? []).map((job) => {
            const busy = busyIds.has(job.id);
            return (
              <li
                key={job.id}
                className="flex items-center gap-4 rounded-xl border border-navy-lighter bg-navy-light px-4 py-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-3">
                    <StatusPill status={job.status} />
                    <span className="text-sm text-white font-medium truncate">
                      {PACKAGE_LABEL[job.package] ?? 'Export'}
                    </span>
                  </div>
                  <p className="mt-1 text-xs text-gray-300">{progressLine(job)}</p>
                  {job.filename && DOWNLOADABLE.has(job.status) && (
                    // The name it saves as, so three exports from one morning
                    // can be told apart before and after downloading (#965).
                    <p className="mt-0.5 text-xs text-gray-400 font-mono truncate">{job.filename}</p>
                  )}
                  <p className="mt-0.5 text-xs text-gray-400">
                    {job.requested_by_email}
                    {job.artifact_bytes ? <> · {formatBytes(job.artifact_bytes)}</> : null}
                    {DOWNLOADABLE.has(job.status) && job.expires_at && (
                      <> · expires {new Date(job.expires_at).toLocaleDateString()}</>
                    )}
                  </p>
                  {job.status === 'failed' && job.error_msg && (
                    <p className="mt-1 text-xs text-rose-300 leading-snug">{job.error_msg}</p>
                  )}
                </div>

                {DOWNLOADABLE.has(job.status) && (
                  <Button
                    variant="primary"
                    size="md"
                    onClick={() => handleDownload(job)}
                    disabled={busy}
                    className="focus-visible:ring-2 focus-visible:ring-amber disabled:opacity-40"
                  >
                    <DownloadIcon className="w-4 h-4 mr-1" />
                    Download
                  </Button>
                )}

                {ACTIVE.has(job.status) && (
                  <button
                    type="button"
                    onClick={() => handleCancel(job)}
                    // The guard is the flag, not the button: a second click
                    // while the first is in flight would post a duplicate
                    // cancel against a row already flagged.
                    disabled={busy || job.cancel_requested}
                    className="px-4 py-2 rounded-lg text-sm text-gray-300 hover:text-white hover:bg-navy-hover transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-amber disabled:opacity-40"
                  >
                    {job.cancel_requested ? 'Cancelling…' : 'Cancel'}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </main>
    </div>
  );
}
